import { Button, mergeClasses, Tooltip } from '@fluentui/react-components'
import {
  CopyRegular,
  DeleteRegular,
  EditArrowBackRegular,
  InsertRegular,
} from '@fluentui/react-icons'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { hasSelectionComments } from '../../api/word/word-service'
import type { MessageRole, MessageSource } from '../../types'
import { Dialog } from '../Dialog/Dialog'
import { MessageSources } from '../MessageSources/MessageSources'
import { useChatBubbleStyles } from './ChatBubble.styles'

interface BubbleActionsProps {
  messageId: string
  messageRole: MessageRole
  timestamp: number
  visible?: boolean
  sources?: MessageSource[]
  /** Undefined `sources` means they failed to load, so this must be false while a reply is still streaming. */
  showSources?: boolean
  onCopy?: (messageId: string) => void
  onEdit?: (messageId: string) => void
  onDelete?: (messageId: string) => void
  onInsert?: (messageId: string) => void
}

const BubbleActions = ({
  messageId,
  messageRole,
  timestamp,
  visible = false,
  sources,
  showSources = false,
  onCopy,
  onEdit,
  onDelete,
  onInsert,
}: BubbleActionsProps) => {
  const { t } = useTranslation()
  const styles = useChatBubbleStyles()
  const [isCommentWarningOpen, setIsCommentWarningOpen] = useState(false)

  const handleInsertClick = async (id: string) => {
    let selectionHasComments: boolean
    try {
      selectionHasComments = await hasSelectionComments()
    } catch {
      // If the Word API call fails we can't confirm comments are absent — show
      // the warning rather than silently proceed and potentially lose comments.
      selectionHasComments = true
    }

    if (selectionHasComments) {
      setIsCommentWarningOpen(true)
    } else {
      onInsert?.(id)
    }
  }

  const handleCommentWarningConfirm = () => {
    onInsert?.(messageId)
    setIsCommentWarningOpen(false)
  }

  const handleCommentWarningCancel = () => {
    setIsCommentWarningOpen(false)
  }

  const formattedTime = new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })

  if (messageRole === 'user') {
    return (
      <div
        aria-hidden={!visible}
        className={
          visible
            ? mergeClasses(styles.actionsWrapper, styles.actionsWrapperVisible)
            : styles.actionsWrapper
        }>
        <span className={styles.timestamp}>{formattedTime}</span>
        {onCopy && (
          <Tooltip content={t('chatInput.copy')} relationship="label" appearance="inverted">
            <Button
              appearance="subtle"
              size="small"
              icon={<CopyRegular />}
              onClick={() => onCopy(messageId)}
              aria-label={t('chatInput.copy')}
            />
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip content={t('chatInput.edit')} relationship="label" appearance="inverted">
            <Button
              appearance="subtle"
              size="small"
              icon={<EditArrowBackRegular />}
              onClick={() => onEdit(messageId)}
              aria-label={t('chatInput.edit')}
            />
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip content={t('delete')} relationship="label" appearance="inverted">
            <Button
              appearance="subtle"
              size="small"
              icon={<DeleteRegular />}
              onClick={() => onDelete(messageId)}
              aria-label={t('delete')}
            />
          </Tooltip>
        )}
      </div>
    )
  }

  /**
   * Assistant message actions (left-aligned bubbles)
   * Shows: insert to Word, copy buttons
   *
   * No edit button for assistant messages - editing is only available
   * for user's own messages to enable resending with modifications.
   *
   * Assistant message actions are always visible (no hover state)
   * as they are key features users should discover easily.
   */
  return (
    <div className={styles.assistantActionsWrapper}>
      {onInsert && (
        <>
          <Tooltip content={t('chatInput.insertToWord')} relationship="label" appearance="inverted">
            <Button
              appearance="subtle"
              size="small"
              icon={<InsertRegular />}
              onClick={() => void handleInsertClick(messageId)}
              aria-label={t('chatInput.insertToWord')}
            />
          </Tooltip>
          <Dialog
            title={t('dialog.insertCommentWarningTitle')}
            content={t('dialog.insertCommentWarningContent')}
            primaryButtonText={t('dialog.insertCommentWarningConfirm')}
            secondaryButtonText={t('cancel')}
            onConfirm={handleCommentWarningConfirm}
            onOpenChange={(open) => {
              if (!open) handleCommentWarningCancel()
            }}
            isDialogOpen={isCommentWarningOpen}
          />
        </>
      )}
      {onCopy && (
        <Tooltip content={t('chatInput.copy')} relationship="label" appearance="inverted">
          <Button
            appearance="subtle"
            size="small"
            icon={<CopyRegular />}
            onClick={() => onCopy(messageId)}
            aria-label={t('chatInput.copy')}
          />
        </Tooltip>
      )}
      {showSources && sources?.length !== 0 && <MessageSources sources={sources} />}
    </div>
  )
}

export { BubbleActions }
